import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import get from 'lodash/get'
import orderBy from 'lodash/orderBy'

import type {
  EmailCampaign,
  EmailCampaignSchedule,
  EmailCampaignStats,
} from 'types/campaign'
import type { Campaign } from 'types/content-editor'
import type { RecipientListId } from 'types/list'
import type { PlaylistId } from 'types/playlist'

import type { CampaignStatus } from '@disco/constants/CampaignTypes'

import {
  STATUS_DRAFT,
  STATUS_SCHEDULED,
  STATUS_SENT,
} from '@disco/constants/CampaignTypes'

import { normalizeString } from '@disco/utils/string-utils'
import {
  formatHumanReadableTimezone,
  setTimeOfDate,
} from '@disco/utils/time-utils'

dayjs.extend(utc)
dayjs.extend(timezone)

/**
 * Returns template_data from a campaign or empty object.
 *
 * @param campaign - Email campaign
 * @returns Template data object
 * @example
 * ```typescript
 * const templateData = getTemplateData(campaign)
// e.g. templateData.subject === 'Monthly Update'
 * ```
 */
export function getTemplateData(campaign: EmailCampaign): any {
  const data = campaign.template_data || {}
  return data
}

/**
 * Creates a new campaign object with updated template_data.
 *
 * @param campaign - Base campaign
 * @param template_data - New template data
 * @returns Updated campaign
 * @example
 * ```typescript
 * const updated = setTemplateData(campaign, {
  subject: 'New Release',
  header_image: 'https://example.com/banner.png'
})
 * ```
 */
export function setTemplateData(
  campaign: EmailCampaign,
  template_data: any,
): EmailCampaign {
  return {
    ...campaign,
    template_data: template_data,
  }
}

/**
 * Retrieves recipients_filter from a campaign.
 *
 * @param campaign - Email campaign
 * @returns Recipient filter object or null
 * @example
 * ```typescript
 * const filter = getRecipientFilter(campaign)
// Example: { rules: [ { ruleType: 'mailingList', ruleValue: '123' } ] }
 * ```
 */
export function getRecipientFilter(
  campaign: EmailCampaign,
): any | null | undefined {
  const data = campaign.recipients_filter || null
  return data
}

/**
 * Resolves sender name/email for a campaign applying sensible fallbacks.
 *
 * @param campaign - Email campaign
 * @param sender - Current user info
 * @param emailGroupsLookup - Available mailing lists for fallback
 * @returns Sender details
 * @example
 * ```typescript
 * const sender = getCampaignSenderWithDefaults(campaign, currentUser, mailingLists)
// sender => { sender_name: 'Alice', sender_email: 'alice@example.com' }
 * ```
 */
export function getCampaignSenderWithDefaults(
  campaign: EmailCampaign,
  sender: {
    first_name: string
    last_name: string
    email: string
    is_support_user: boolean
  },
  // only id + sender fields are read, so a recipient-less mailing list is enough
  emailGroupsLookup: Array<{
    id: RecipientListId
    sender_name?: string
    sender_email?: string
  }>,
): {
  sender_email: string
  sender_name: string
} {
  let { sender_name, sender_email } = campaign
  if (!sender_name || !sender_email) {
    const { recipients_filter } = campaign
    if (recipients_filter) {
      try {
        // recipients_filter is untyped server json, so wrap in try/catch
        const mailingListRule = recipients_filter.rules.find(
          r => r.ruleType === 'mailingList' && r.ruleValue,
        )
        // multiple `mailingList` rules are allowed, only the first is chosen
        if (mailingListRule) {
          const listId = parseInt(mailingListRule.ruleValue)
          const list = emailGroupsLookup.find(g => g.id === listId)
          if (list) {
            // fallback #1: Mailing List from previous step
            sender_name = sender_name || list.sender_name || ''
            sender_email = sender_email || list.sender_email || ''
          }
        }
      } catch {
        return { sender_name: '', sender_email: '' }
      }
    }
    if (!sender.is_support_user) {
      // fallback #2: profile info
      const fullname = `${sender.first_name} ${sender.last_name}`.trim()
      sender_name = sender_name || fullname
      sender_email = sender_email || sender.email
    }
  }
  return { sender_name: sender_name || '', sender_email: sender_email || '' }
}

/**
 * Computes difference in statistic value from previous campaign.
 *
 * @param stats - Campaign stats object
 * @param param - Stat key, e.g., 'open_rate'
 * @returns Previous campaign value for the given param (0 if stats unavailable)
 * @example
 * ```typescript
 * const clickRateDelta = getCampaignStatsDiff(campaign.stats, 'click_rate')
// Previous campaign click rate value
 * ```
 */
export const getCampaignStatsDiff = (
  stats: Partial<EmailCampaignStats> | null | undefined,
  param: string,
): number => {
  if (!stats) return 0
  const prevParam = get(stats, `change_from_last_campaign.${param}`, 0)
  // const diff = Math.round(
  //   (get(stats, param, 0) - prevParam) / (prevParam || 1) * 100,
  // )
  return prevParam
}

/**
 * Checks that all playlist content in a campaign is already loaded.
 *
 * @param campaign - Campaign definition
 * @param campaignPlaylists - Map of loaded playlists
 * @returns Boolean indicating whether all playlists are loaded
 * @example
 * ```typescript
 * const ready = isCampaignLoaded(campaign, playlistMap);
 * ```
 */
export const isCampaignLoaded = (
  campaign: Campaign,
  campaignPlaylists?: Partial<Record<PlaylistId, any>> | null,
): boolean => {
  const playlistIds: Array<number> = []
  if (!campaign || !campaign.sections) return false

  campaign.sections.forEach(s => {
    s.columns.forEach(c => {
      if (c.columnContent) {
        c.columnContent.forEach(content => {
          if (content.type === 'playlist' && content.playlist_id)
            playlistIds.push(content.playlist_id)
        })
      }
    })
  })

  return playlistIds.filter(id => !(campaignPlaylists || {})[id]).length === 0
}

/**
 * Formats a timezone offset string for display.
 *
 * @param date_time - Source ISO date string
 * @param timezone - IANA timezone id
 * @returns Offset like '+05:00'
 * @example
 * ```typescript
 * const offset = formatTimezoneOffset('2023-05-05T12:00:00Z', 'America/Los_Angeles');
 * ```
 */
export const formatTimezoneOffset = (
  date_time: string,
  timezone: string,
): string => {
  const offsetInMinutes = dayjs(date_time).tz(timezone).utcOffset()
  const timeZoneOffset = dayjs(date_time).utcOffset(offsetInMinutes).format('Z')
  return timeZoneOffset
}

/**
 * Produces an ISO string with timezone offset for scheduling emails.
 *
 * @param date - Date part
 * @param time - Time (HH:mm)
 * @param timeZone - IANA timezone id
 * @returns ISO-8601 string including zone offset
 * @example
 * ```typescript
 * const date = new Date('2023-05-05') // May 5 2023 in local timezone
const iso = getTimeDateToScheduleEmail(date, '09:00', 'Europe/London')
// iso => '2023-05-05T09:00:00+01:00'
 * ```
 */

export const getTimeDateToScheduleEmail = (
  date: Date,
  time: string,
  timeZone: string,
): string => {
  const selectedLocalDateTime = dayjs.tz(
    `${dayjs(date).format('YYYY-MM-DD')} ${time}`,
    'YYYY-MM-DD HH:mm',
    timeZone,
  )

  return selectedLocalDateTime.format('YYYY-MM-DDTHH:mm:ssZ')
}
/**
 * Returns new campaign object with scheduled_at fields set.
 *
 * @param campaign - Campaign to update
 * @param data - Object containing date, time and timeZone selections
 * @example
 * ```typescript
 * const scheduled = updateCampaignSchedule(campaign, {
  date: new Date('2023-05-10'),
  time: { value: '15:00' },
  timeZone: { value: 'America/New_York' }
})
 * ```
 */
export const updateCampaignSchedule = (
  campaign: EmailCampaign,
  data: any,
): EmailCampaign => {
  const { date, time, timeZone } = data || {}
  return {
    ...campaign,
    scheduled_at: getTimeDateToScheduleEmail(date, time.value, timeZone.value),
    scheduled_at_timezone: timeZone?.value,
    status: 'scheduled',
  }
}

/**
 * Resets scheduling fields turning the campaign back into draft.
 *
 * @param campaign - Campaign to reset
 * @returns Updated campaign in draft status
 * @example
 * ```typescript
 * const draft = cancelSchedule(scheduledCampaign);
 * ```
 */
export const cancelSchedule = (campaign: EmailCampaign): EmailCampaign => {
  return {
    ...campaign,
    scheduled_at: null,
    scheduled_at_timezone: null,
    status: 'draft',
  }
}

/**
 * Converts raw scheduled_at fields into UI-friendly date/time/timezone objects used by UI controls.
 *
 * @param campaign - Campaign containing scheduling fields
 * @returns Structured schedule object or null when campaign is not scheduled
 * @example
 * ```typescript
 * const sched = formatScheduledCampaignData(campaign);
 * ```
 */
export const formatScheduledCampaignData = (campaign: {
  scheduled_at: string | null
  scheduled_at_timezone: string | null
  status: 'draft' | 'scheduled' | 'sent'
}): EmailCampaignSchedule | null => {
  if (!campaign.scheduled_at || !campaign.scheduled_at_timezone) return null
  const timezonelabel =
    campaign.scheduled_at_timezone &&
    formatHumanReadableTimezone(campaign.scheduled_at_timezone)
  const timeInTimeZone = dayjs
    .utc(campaign.scheduled_at)
    .tz(campaign.scheduled_at_timezone)

  const scheduled_at_date = {
    value: timeInTimeZone.format('YYYY-MM-DD'),
    label: timeInTimeZone.format('MMMM D, YYYY'),
  } as const
  const scheduled_at_time = {
    value: timeInTimeZone.format('HH:mm'),
    label: timeInTimeZone.format('h:mm A'),
  } as const
  const scheduled_at_timezone = {
    value: campaign.scheduled_at_timezone,
    label: timezonelabel?.label,
  } as const

  return {
    date: scheduled_at_date,
    time: scheduled_at_time,
    timeZone: scheduled_at_timezone,
    status: campaign.status,
  }
}

/**
 * Filters, sorts, and limits campaigns for table display.
 *
 * @param options.emailCampaigns - Full list of campaigns
 * @param options.limit - Maximum number of campaigns to return
 * @param options.status - Status to filter by
 * @returns Object containing `emailsCount` (after filtering) and `tableData` (the sliced/sorted campaigns)
 * @example
 * ```typescript
 * const { tableData } = getCampaignsTableData({ emailCampaigns, limit: 5, status: 'draft' });
 * ```
 */
export const getCampaignsTableData = ({
  emailCampaigns,
  limit,
  status,
}: {
  emailCampaigns: Array<EmailCampaign>
  limit?: number
  status: CampaignStatus
}): {
  emailsCount: number
  tableData: Array<EmailCampaign>
} => {
  let emailsCount = emailCampaigns.length
  let tableData = emailCampaigns

  if (status) {
    tableData = emailCampaigns.filter(campaign => campaign.status === status)
    emailsCount = tableData.length
  }

  if (limit) {
    switch (status) {
      case STATUS_DRAFT:
        // Show up to {limit} of the last edited email drafts
        tableData = orderBy(
          tableData,
          campaign => campaign.last_modified,
          'desc',
        )
        break
      case STATUS_SCHEDULED:
        // Show up to {limit} of the soonest {limit} Scheduled emails
        tableData = orderBy(
          tableData,
          campaign =>
            dayjs.tz(
              dayjs(campaign.scheduled_at),
              campaign.scheduled_at_timezone || undefined,
            ),
          'asc',
        )

        break
      case STATUS_SENT:
        // Show up to {limit} of the latest Sent emails
        tableData = orderBy(tableData, campaign => campaign.sent_at, 'desc')
        break

      default:
        break
    }
    tableData = tableData.slice(0, limit)
  }
  return { emailsCount, tableData }
}

/**
 * Filters an array of email campaigns by name (case-insensitive substring match) and optional status.
 *
 * @param campaigns - Array of email campaigns to search
 * @param q - (Optional) Search query string; if empty or null the original array is returned
 * @param status - (Optional) Campaign status to filter by (`'draft'`, `'scheduled'`, `'sent'`, etc.)
 * @returns Filtered array containing campaigns whose `name` matches the query and whose `status` matches (if provided)
 *
 * @example
 * ```typescript
 * // Return sent campaigns whose name contains "newsletter"
 * const results = searchCampaigns(allCampaigns, 'newsletter', 'sent');
 * ```
 */
export const searchCampaigns = (
  campaigns: Array<EmailCampaign>,
  q?: string | null,
  status?: CampaignStatus | null,
): Array<EmailCampaign> => {
  let result = campaigns
  if (!q) return result
  if (status) {
    result = result.filter(campaign => campaign.status === status)
  }

  return result.filter(campaign => {
    const campaignName = campaign.name || `Untitled ${campaign.type}`

    return normalizeString(campaignName).includes(normalizeString(q))
  })
}
